import {
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { Button } from './ui/Button';
import {
  normalizeSupportInterestedUser,
  supportUserAvatarColor,
  supportUserInitials,
} from '../utils/supportUserDisplay';

const { semantic } = colorTokens;

const dialogPaperSx = {
  width: 'min(440px, calc(100vw - 48px))',
  maxWidth: 'min(440px, calc(100vw - 48px))',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

/**
 * Compact candidate picker for Portal add-involved (S7-C).
 */
export function PortalSupportAddInvolvedDialog({
  open,
  onClose,
  candidates = [],
  loading = false,
  submitting = false,
  emptyLabel,
  title,
  cancelLabel,
  loadingLabel,
  onSelect,
}) {
  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!submitting) onClose?.();
      }}
      maxWidth={false}
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          fontFamily: typographyTokens.fontFamily,
          fontWeight: typographyTokens.fontWeightSemibold,
          fontSize: 16,
          color: semantic.text.primary,
          borderBottom: `1px solid ${semantic.border.subtle}`,
        }}
      >
        {title}
      </DialogTitle>
      <DialogContent sx={{ px: 1, py: 1, minHeight: 120 }}>
        {loading ? (
          <Typography sx={{ px: 2, py: 2, fontSize: 13, color: semantic.text.muted }}>
            {loadingLabel}
          </Typography>
        ) : candidates.length === 0 ? (
          <Typography
            sx={{ px: 2, py: 2, fontSize: 13, color: semantic.text.muted }}
            data-testid="portal-support-no-candidates"
          >
            {emptyLabel}
          </Typography>
        ) : (
          <List dense disablePadding>
            {candidates.map((raw) => {
              const user = normalizeSupportInterestedUser(raw);
              const label = user.name || String(user.id);
              const initials = supportUserInitials(user.name);
              const bg = supportUserAvatarColor(user.name || user.id);
              return (
                <ListItemButton
                  key={String(user.id)}
                  disabled={submitting}
                  onClick={() => onSelect?.(user.id)}
                  data-testid={`portal-support-candidate-${user.id}`}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={user.avatarUrl || undefined}
                      sx={{
                        bgcolor: user.avatarUrl ? undefined : bg,
                        width: 32,
                        height: 32,
                        fontSize: 12,
                      }}
                    >
                      {!user.avatarUrl ? initials : null}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={label}
                    primaryTypographyProps={{ sx: { color: semantic.text.primary, fontSize: 14 } }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5, borderTop: `1px solid ${semantic.border.subtle}` }}>
        <Button variant="Ghost" onClick={onClose} disabled={submitting}>
          {cancelLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

